import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'new' | 'in_discussion' | 'quoted' | 'won' | 'lost';
type Source = 'phone' | 'email' | 'website' | 'reference' | 'exhibition' | 'visit' | 'other';
type Mode = 'call' | 'email' | 'visit' | 'meeting' | 'other';

interface EnquiryRow {
  id: string;
  enquiryNumber: string;
  enquiryDate: string;
  customerId: string | null;
  partyName: string;
  isProspect: boolean;
  contactPerson: string | null;
  contactPhone: string | null;
  city: string | null;
  source: Source;
  subject: string;
  expectedValue: number | null;
  expectedCloseDate: string | null;
  status: Status;
  nextFollowUpOn: string | null;
  followUpDue: boolean;
  ownerUserId: string | null;
  ownerName: string | null;
  salesOrderId: string | null;
  quoteNumber: string | null;
  quoteValue: number | null;
}
interface EnquiryPage {
  data: EnquiryRow[];
  total: number;
  totalPages: number;
}
interface EnquiryItem {
  id: string;
  productId: string | null;
  description: string;
  sku: string | null;
  unit: string | null;
  quantity: number;
  targetPrice: number | null;
}
interface Enquiry extends EnquiryRow {
  prospectName: string | null;
  contactEmail: string | null;
  requirement: string | null;
  lostReason: string | null;
  createdBy: string | null;
  items: EnquiryItem[];
  followUps: { id: string; followUpDate: string; mode: Mode; notes: string; byName: string | null }[];
}
interface Summary {
  open: number;
  openValue: number;
  followUpsDue: number;
  quoted: number;
  wonThisMonth: number;
  lostThisMonth: number;
  winRate: number | null;
}
interface Choice {
  id: string;
  name: string;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
}
interface FormLine {
  productId: string | null;
  description: string;
  sku: string;
  unit: string;
  quantity: string;
  targetPrice: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  in_discussion: { label: 'In discussion', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  quoted: { label: 'Quoted', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  won: { label: 'Won', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  lost: { label: 'Lost', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};
const SOURCES: Record<Source, string> = { phone: 'Phone', email: 'Email', website: 'Website', reference: 'Reference', exhibition: 'Exhibition', visit: 'Visit', other: 'Other' };
const MODES: Record<Mode, string> = { call: 'Call', email: 'Email', visit: 'Visit', meeting: 'Meeting', other: 'Other' };

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));

const enquiryApi = {
  page: async (params: object): Promise<EnquiryPage> => (await api.get('/enquiries', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/enquiries/summary')).data,
  owners: async (): Promise<Choice[]> => (await api.get('/enquiries/owners')).data,
  one: async (id: string): Promise<Enquiry> => (await api.get(`/enquiries/${id}`)).data,
  create: async (data: object): Promise<Enquiry> => (await api.post('/enquiries', data)).data,
  update: async (id: string, data: object): Promise<Enquiry> => (await api.put(`/enquiries/${id}`, data)).data,
  act: async (id: string, action: string, data: object = {}): Promise<Enquiry> => (await api.post(`/enquiries/${id}/${action}`, data)).data,
  customers: async (): Promise<Choice[]> => {
    const result = (await api.get('/customers', { params: { limit: 100 } })).data;
    return (Array.isArray(result) ? result : result?.data ?? []) as Choice[];
  },
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

// ---------------------------------------------------------------- log or change an enquiry

const EnquiryForm: React.FC<{ enquiry: Enquiry | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ enquiry, isPending, onCancel, onSubmit }) => {
  const { data: customers = [] } = useQuery({ queryKey: ['enquiries', 'customers'], queryFn: enquiryApi.customers });
  const { data: owners = [] } = useQuery({ queryKey: ['enquiries', 'owners'], queryFn: enquiryApi.owners });
  const [from, setFrom] = useState<'customer' | 'prospect'>(enquiry && !enquiry.isProspect ? 'customer' : 'prospect');
  const [form, setForm] = useState({
    customerId: enquiry?.customerId ?? '',
    prospectName: enquiry?.prospectName ?? '',
    contactPerson: enquiry?.contactPerson ?? '',
    contactPhone: enquiry?.contactPhone ?? '',
    contactEmail: enquiry?.contactEmail ?? '',
    city: enquiry?.city ?? '',
    source: (enquiry?.source ?? 'phone') as Source,
    subject: enquiry?.subject ?? '',
    requirement: enquiry?.requirement ?? '',
    expectedValue: enquiry?.expectedValue === null || enquiry?.expectedValue === undefined ? '' : String(enquiry.expectedValue),
    expectedCloseDate: enquiry?.expectedCloseDate ?? '',
    nextFollowUpOn: enquiry?.nextFollowUpOn ?? '',
    ownerUserId: enquiry?.ownerUserId ?? '',
  });
  const [lines, setLines] = useState<FormLine[]>(() =>
    (enquiry?.items ?? []).map((item) => ({ productId: item.productId, description: item.description, sku: item.sku ?? '', unit: item.unit ?? '', quantity: String(item.quantity), targetPrice: item.targetPrice === null ? '' : String(item.targetPrice) })),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: found = [], isFetching } = useQuery({ queryKey: ['enquiries', 'products', search], queryFn: () => enquiryApi.products(search), enabled: search.length >= 2 });
  const choices = found.filter((product) => !lines.some((line) => line.productId === product.id));
  // Once a quotation exists the enquiry stays with its customer
  const customerLocked = !!enquiry?.salesOrderId;

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const setLine = (index: number, patch: Partial<FormLine>) => {
    setLines((prev) => prev.map((line, position) => (position === index ? { ...line, ...patch } : line)));
    setErrors((prev) => {
      const { [`line-${index}`]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (from === 'customer' && !form.customerId) next.customerId = 'Choose the customer';
    if (from === 'prospect' && form.prospectName.trim().length < 2) next.prospectName = 'Name the prospect';
    if (form.subject.trim().length < 3) next.subject = 'Say in a line what is being asked for';
    if (form.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) next.contactEmail = 'Enter a valid email address';
    if (form.expectedValue.trim() !== '' && (!Number.isFinite(Number(form.expectedValue)) || Number(form.expectedValue) < 0)) next.expectedValue = 'Enter an amount';
    if (form.nextFollowUpOn && form.nextFollowUpOn < today()) next.nextFollowUpOn = 'Not in the past';
    lines.forEach((line, index) => {
      const quantity = Number(line.quantity);
      if (!line.description.trim()) next[`line-${index}`] = 'Describe the item';
      else if (line.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next[`line-${index}`] = 'Enter a quantity above zero, up to 4 decimals';
      else if (line.targetPrice.trim() !== '' && (!Number.isFinite(Number(line.targetPrice)) || Number(line.targetPrice) < 0)) next[`line-${index}`] = 'Enter a price';
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      customerId: from === 'customer' ? form.customerId : null,
      prospectName: from === 'prospect' ? form.prospectName.trim() : null,
      contactPerson: form.contactPerson.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      contactEmail: form.contactEmail.trim() || null,
      city: form.city.trim() || null,
      source: form.source,
      subject: form.subject.trim(),
      requirement: form.requirement.trim() || null,
      expectedValue: form.expectedValue.trim() === '' ? null : Number(form.expectedValue),
      expectedCloseDate: form.expectedCloseDate || null,
      nextFollowUpOn: form.nextFollowUpOn || null,
      ownerUserId: form.ownerUserId || undefined,
      items: lines.map((line) => ({ productId: line.productId, description: line.description.trim(), quantity: Number(line.quantity), targetPrice: line.targetPrice.trim() === '' ? null : Number(line.targetPrice) })),
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Who the enquiry is from">
        {(['prospect', 'customer'] as const).map((kind, index) => (
          <button
            key={kind}
            type="button"
            aria-pressed={from === kind}
            disabled={customerLocked}
            onClick={() => setFrom(kind)}
            className={cn('px-3 py-1.5 text-sm font-semibold ring-1 ring-inset disabled:opacity-60', index === 0 ? 'rounded-l-md' : '-ml-px rounded-r-md', from === kind ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50')}
          >
            {kind === 'prospect' ? 'From a prospect' : 'From a customer'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {from === 'customer' ? (
          <Field label="Customer" htmlFor="eq-customer" required error={errors.customerId} className="sm:col-span-2" hint={customerLocked ? 'A quotation has been raised; the customer cannot change' : undefined}>
            <select id="eq-customer" value={form.customerId} disabled={customerLocked} onChange={set('customerId')} className={cn(inputClass, errors.customerId && inputErrorClass)}>
              <option value="">Choose…</option>
              {customers.map((customer) => (<option key={customer.id} value={customer.id}>{customer.name}</option>))}
            </select>
          </Field>
        ) : (
          <Field label="Prospect" htmlFor="eq-prospect" required error={errors.prospectName} className="sm:col-span-2" hint="A company that is not a customer yet">
            <input id="eq-prospect" type="text" maxLength={200} value={form.prospectName} onChange={set('prospectName')} className={cn(inputClass, errors.prospectName && inputErrorClass)} />
          </Field>
        )}
        <Field label="Contact person" htmlFor="eq-contact">
          <input id="eq-contact" type="text" maxLength={100} value={form.contactPerson} onChange={set('contactPerson')} className={inputClass} />
        </Field>
        <Field label="Phone" htmlFor="eq-phone">
          <input id="eq-phone" type="text" maxLength={30} value={form.contactPhone} onChange={set('contactPhone')} className={inputClass} />
        </Field>
        <Field label="Email" htmlFor="eq-email" error={errors.contactEmail}>
          <input id="eq-email" type="email" maxLength={150} value={form.contactEmail} onChange={set('contactEmail')} className={cn(inputClass, errors.contactEmail && inputErrorClass)} />
        </Field>
        <Field label="City" htmlFor="eq-city">
          <input id="eq-city" type="text" maxLength={100} value={form.city} onChange={set('city')} className={inputClass} />
        </Field>
        <Field label="What is asked for" htmlFor="eq-subject" required error={errors.subject} className="sm:col-span-2">
          <input id="eq-subject" type="text" maxLength={200} value={form.subject} onChange={set('subject')} className={cn(inputClass, errors.subject && inputErrorClass)} placeholder="500 flanges, 4 inch, per drawing" />
        </Field>
        <Field label="Details" htmlFor="eq-requirement" className="sm:col-span-2">
          <textarea id="eq-requirement" rows={2} maxLength={4000} value={form.requirement} onChange={set('requirement')} className={inputClass} />
        </Field>
        <Field label="Came through" htmlFor="eq-source">
          <select id="eq-source" value={form.source} onChange={set('source')} className={inputClass}>
            {(Object.keys(SOURCES) as Source[]).map((source) => (<option key={source} value={source}>{SOURCES[source]}</option>))}
          </select>
        </Field>
        <Field label="Looked after by" htmlFor="eq-owner" hint={enquiry ? undefined : 'Left empty: you'}>
          <select id="eq-owner" value={form.ownerUserId} onChange={set('ownerUserId')} className={inputClass}>
            <option value="">{enquiry ? 'Nobody' : 'Me'}</option>
            {owners.map((owner) => (<option key={owner.id} value={owner.id}>{owner.name}</option>))}
          </select>
        </Field>
        <Field label="Expected value (₹)" htmlFor="eq-value" error={errors.expectedValue}>
          <input id="eq-value" type="number" min="0" step="0.01" value={form.expectedValue} onChange={set('expectedValue')} className={cn(inputClass, 'text-right tabular-nums', errors.expectedValue && inputErrorClass)} />
        </Field>
        <Field label="Expected to close by" htmlFor="eq-close">
          <input id="eq-close" type="date" value={form.expectedCloseDate} onChange={set('expectedCloseDate')} className={inputClass} />
        </Field>
        <Field label="Next follow-up" htmlFor="eq-next" error={errors.nextFollowUpOn}>
          <input id="eq-next" type="date" min={today()} value={form.nextFollowUpOn} onChange={set('nextFollowUpOn')} className={cn(inputClass, errors.nextFollowUpOn && inputErrorClass)} />
        </Field>
      </div>

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Items asked for · {lines.length}</h4>
        {lines.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {lines.map((line, index) => (
              <li key={index} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  {line.productId ? (
                    <p className="min-w-0 flex-1 text-sm text-gray-900">
                      {line.description} <span className="text-xs text-gray-500">{line.sku}</span>
                    </p>
                  ) : (
                    <input type="text" maxLength={255} value={line.description} onChange={(event) => setLine(index, { description: event.target.value })} placeholder="Describe the item" aria-label={`Description of item ${index + 1}`} className={cn(inputClass, 'min-w-0 flex-1 py-1')} />
                  )}
                  <input type="number" min="0" step={QUANTITY_STEP} value={line.quantity} onChange={(event) => setLine(index, { quantity: event.target.value })} placeholder="Qty" aria-label={`Quantity of ${line.description || `item ${index + 1}`}`} className={cn(inputClass, 'w-24 py-1 text-right tabular-nums')} />
                  <input type="number" min="0" step="0.01" value={line.targetPrice} onChange={(event) => setLine(index, { targetPrice: event.target.value })} placeholder="Target ₹" aria-label={`Target price of ${line.description || `item ${index + 1}`}`} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums')} />
                  <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${line.description || `item ${index + 1}`}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {errors[`line-${index}`] && <p className="text-sm text-red-600">{errors[`line-${index}`]}</p>}
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-col gap-2 border-t border-gray-200 p-3 sm:flex-row sm:items-start">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input id="eq-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to add" aria-label="Search products to add" className={cn(inputClass, 'pl-9')} />
            </div>
            {search.length >= 2 && (
              <ul className="mt-1 max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                {choices.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
                ) : (
                  choices.map((product) => (
                    <li key={product.id}>
                      <button type="button" onClick={() => { setLines((prev) => [...prev, { productId: product.id, description: product.name, sku: product.sku, unit: product.unit, quantity: '', targetPrice: '' }]); setSearchInput(''); setSearch(''); }} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                        <span>
                          <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                        </span>
                        <span className="text-xs text-gray-500">{product.unit}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          <button type="button" onClick={() => setLines((prev) => [...prev, { productId: null, description: '', sku: '', unit: '', quantity: '', targetPrice: '' }])} className={secondaryButtonClass}>
            Add an Item Not in the Master
          </button>
        </div>
      </section>

      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : enquiry ? 'Save Enquiry' : 'Log Enquiry'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one enquiry

const EnquiryPanel: React.FC<{ enquiryId: string; canEdit: boolean; canQuote: boolean; onEdit: (enquiry: Enquiry) => void; onClose: () => void; onChanged: () => void }> = ({ enquiryId, canEdit, canQuote, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: enquiry, isLoading, isError, error } = useQuery({ queryKey: ['enquiries', 'one', enquiryId], queryFn: () => enquiryApi.one(enquiryId) });
  const [followUp, setFollowUp] = useState({ mode: 'call' as Mode, notes: '', nextFollowUpOn: '' });
  const [followUpError, setFollowUpError] = useState('');
  const [losing, setLosing] = useState(false);
  const [lostReason, setLostReason] = useState('');
  // Raising a quotation asks for the delivery date it will carry
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState({ expectedDeliveryDate: '', quoteValidUntil: '' });

  const act = useMutation({
    mutationFn: ({ action, data }: { action: string; data?: object }) => enquiryApi.act(enquiryId, action, data),
    onSuccess: (saved, { action }) => {
      const message: Record<string, string> = {
        'follow-ups': 'Follow-up recorded',
        quote: `Quotation ${saved.quoteNumber ?? ''} raised`,
        won: `${saved.enquiryNumber} marked as won`,
        lost: `${saved.enquiryNumber} marked as lost`,
        reopen: `${saved.enquiryNumber} reopened`,
      };
      toast.success(message[action]);
      queryClient.setQueryData(['enquiries', 'one', enquiryId], saved);
      if (action === 'follow-ups') setFollowUp({ mode: 'call', notes: '', nextFollowUpOn: '' });
      if (action === 'quote') queryClient.invalidateQueries({ queryKey: ['salesOrders'] });
      setLosing(false);
      setLostReason('');
      setQuoting(false);
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 }),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !enquiry) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The enquiry could not be loaded.')}</p>;
  const open = ['new', 'in_discussion', 'quoted'].includes(enquiry.status);

  const handleFollowUp = (event: React.FormEvent) => {
    event.preventDefault();
    if (followUp.notes.trim().length < 3) {
      setFollowUpError('Say what was discussed');
      return;
    }
    setFollowUpError('');
    act.mutate({ action: 'follow-ups', data: { mode: followUp.mode, notes: followUp.notes.trim(), nextFollowUpOn: followUp.nextFollowUpOn || null } });
  };

  return (
    <div className="space-y-4">
      <DetailCard title={enquiry.subject}>
        <DetailItem label={enquiry.isProspect ? 'Prospect' : 'Customer'}>{enquiry.partyName}</DetailItem>
        <DetailItem label="Contact">{[enquiry.contactPerson, enquiry.contactPhone, enquiry.contactEmail].filter(Boolean).join(' · ') || '—'}</DetailItem>
        <DetailItem label="Status"><span className={cn(badgeClass, STATUS[enquiry.status].className)}>{STATUS[enquiry.status].label}</span></DetailItem>
        <DetailItem label="Came through">{SOURCES[enquiry.source]}{enquiry.city ? ` · ${enquiry.city}` : ''}</DetailItem>
        <DetailItem label="Expected value">{enquiry.expectedValue === null ? '—' : formatMoney(enquiry.expectedValue)}</DetailItem>
        <DetailItem label="Expected to close">{formatDate(enquiry.expectedCloseDate)}</DetailItem>
        <DetailItem label="Looked after by">{enquiry.ownerName || '—'}</DetailItem>
        <DetailItem label="Next follow-up"><span className={enquiry.followUpDue ? 'text-red-600' : undefined}>{formatDate(enquiry.nextFollowUpOn)}</span></DetailItem>
        {enquiry.requirement && <DetailItem label="Details" className="col-span-2 lg:col-span-4"><span className="whitespace-pre-line font-normal">{enquiry.requirement}</span></DetailItem>}
        {enquiry.quoteNumber && (
          <DetailItem label="Quotation" className="col-span-2">
            <Link to="/quotes" className="text-indigo-600 hover:text-indigo-500">{enquiry.quoteNumber}</Link>
            {enquiry.quoteValue !== null && <span className="font-normal text-gray-600"> · {formatMoney(enquiry.quoteValue)}</span>}
          </DetailItem>
        )}
        {enquiry.lostReason && <DetailItem label="Lost because" className="col-span-2"><span className="font-normal">{enquiry.lostReason}</span></DetailItem>}
      </DetailCard>

      <section className="overflow-hidden rounded-lg border border-gray-200">
        <h4 className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Items asked for · {enquiry.items.length}</h4>
        {enquiry.items.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500">None listed.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {enquiry.items.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                <span className="text-gray-900">
                  {item.description} {item.sku && <span className="text-xs text-gray-500">{item.sku}</span>}
                  {!item.productId && <span className={cn(badgeClass, 'ml-2 bg-amber-50 text-amber-800 ring-amber-600/20')}>Not in the item master</span>}
                </span>
                <span className="shrink-0 text-gray-900 tabular-nums">
                  {formatNumber(item.quantity)} {item.unit ?? ''}
                  {item.targetPrice !== null && <span className="text-gray-500"> · target {formatMoney(item.targetPrice)}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-gray-200">
        <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Follow-ups · {enquiry.followUps.length}</h4>
        {open && canEdit && (
          <form onSubmit={handleFollowUp} noValidate className="grid grid-cols-1 gap-3 border-b border-gray-200 p-3 sm:grid-cols-6">
            <Field label="How" htmlFor="fu-mode">
              <select id="fu-mode" value={followUp.mode} onChange={(event) => setFollowUp((prev) => ({ ...prev, mode: event.target.value as Mode }))} className={inputClass}>
                {(Object.keys(MODES) as Mode[]).map((mode) => (<option key={mode} value={mode}>{MODES[mode]}</option>))}
              </select>
            </Field>
            <Field label="What was discussed" htmlFor="fu-notes" required error={followUpError} className="sm:col-span-3">
              <input id="fu-notes" type="text" maxLength={1000} value={followUp.notes} onChange={(event) => { setFollowUp((prev) => ({ ...prev, notes: event.target.value })); setFollowUpError(''); }} className={cn(inputClass, followUpError && inputErrorClass)} />
            </Field>
            <Field label="Follow up next on" htmlFor="fu-next">
              <input id="fu-next" type="date" min={today()} value={followUp.nextFollowUpOn} onChange={(event) => setFollowUp((prev) => ({ ...prev, nextFollowUpOn: event.target.value }))} className={inputClass} />
            </Field>
            <div className="flex items-end">
              <button type="submit" disabled={act.isPending} className={cn(secondaryButtonClass, 'w-full justify-center')}>
                Record
              </button>
            </div>
          </form>
        )}
        {enquiry.followUps.length === 0 ? (
          <p className="px-3 py-3 text-sm text-gray-500">Nothing recorded yet.</p>
        ) : (
          <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto">
            {enquiry.followUps.map((item) => (
              <li key={item.id} className="px-3 py-2 text-sm">
                <p className="text-gray-900">{item.notes}</p>
                <p className="mt-0.5 text-xs text-gray-500">{[formatDate(item.followUpDate), MODES[item.mode], item.byName].filter(Boolean).join(' · ')}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {quoting ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="mb-3 text-sm text-gray-700">The quotation is raised for {enquiry.partyName} with the items above. Prices start from the target price where one is given, otherwise from the price list.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Expected delivery" htmlFor="ep-delivery" required>
              <input id="ep-delivery" type="date" min={today()} value={quote.expectedDeliveryDate} onChange={(event) => setQuote((prev) => ({ ...prev, expectedDeliveryDate: event.target.value }))} className={inputClass} />
            </Field>
            <Field label="Prices valid until" htmlFor="ep-valid" hint="Optional">
              <input id="ep-valid" type="date" min={today()} value={quote.quoteValidUntil} onChange={(event) => setQuote((prev) => ({ ...prev, quoteValidUntil: event.target.value }))} className={inputClass} />
            </Field>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setQuoting(false)} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" disabled={act.isPending || !quote.expectedDeliveryDate} onClick={() => act.mutate({ action: 'quote', data: { expectedDeliveryDate: quote.expectedDeliveryDate, quoteValidUntil: quote.quoteValidUntil || null } })} className={primaryButtonClass}>
              {act.isPending ? 'Working…' : 'Confirm Quotation'}
            </button>
          </div>
        </div>
      ) : losing ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label="Why was it lost?" htmlFor="ep-lost" required>
            <input id="ep-lost" type="text" maxLength={300} value={lostReason} onChange={(event) => setLostReason(event.target.value)} className={inputClass} placeholder="Price, delivery, went to a competitor…" />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setLosing(false); setLostReason(''); }} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" disabled={act.isPending || lostReason.trim().length < 3} onClick={() => act.mutate({ action: 'lost', data: { reason: lostReason.trim() } })} className={dangerButtonClass}>
              Confirm Lost
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close Window
          </button>
          {enquiry.status === 'lost' && canEdit && (
            <button type="button" onClick={() => act.mutate({ action: 'reopen' })} disabled={act.isPending} className={secondaryButtonClass}>
              Reopen
            </button>
          )}
          {open && canEdit && (
            <>
              <button type="button" onClick={() => onEdit(enquiry)} className={secondaryButtonClass}>
                Edit
              </button>
              <button type="button" onClick={() => setLosing(true)} disabled={act.isPending} className={secondaryButtonClass}>
                Mark Lost
              </button>
              <button type="button" onClick={() => act.mutate({ action: 'won' })} disabled={act.isPending} className={secondaryButtonClass}>
                Mark Won
              </button>
            </>
          )}
          {open && !enquiry.salesOrderId && canQuote && (
            <button type="button" onClick={() => { setQuote({ expectedDeliveryDate: enquiry.expectedCloseDate && enquiry.expectedCloseDate >= today() ? enquiry.expectedCloseDate : '', quoteValidUntil: '' }); setQuoting(true); }} disabled={act.isPending} className={primaryButtonClass}>
              Raise Quotation
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Enquiries: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('sales.create');
  const canEdit = can('sales.update');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | 'open' | ''>('open');
  const [followUpDue, setFollowUpDue] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Enquiry | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['enquiries', 'page', { page, limit, search, status: statusFilter, followUpDue }],
    queryFn: () => enquiryApi.page({ page, limit, search, status: statusFilter, followUpDue }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['enquiries', 'summary'], queryFn: enquiryApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['enquiries', 'one', viewingId], queryFn: () => enquiryApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['enquiries', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => (editing ? enquiryApi.update(editing.id, input) : enquiryApi.create(input)),
    onSuccess: (enquiry) => {
      toast.success(editing ? `${enquiry.enquiryNumber} saved` : `${enquiry.enquiryNumber} logged`);
      queryClient.setQueryData(['enquiries', 'one', enquiry.id], enquiry);
      refreshLists();
      closeForm();
      setViewingId(enquiry.id);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The enquiry could not be saved')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || statusFilter !== 'open' || followUpDue;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Open enquiries', value: formatNumber(summary?.open ?? 0), hint: `Worth ${formatMoney(summary?.openValue ?? 0)} if all are won` },
    { label: 'Follow-ups due', value: formatNumber(summary?.followUpsDue ?? 0), hint: 'Due today or overdue' },
    { label: 'Quoted', value: formatNumber(summary?.quoted ?? 0), hint: 'Waiting for the customer' },
    { label: 'Win rate', value: summary?.winRate === null || summary?.winRate === undefined ? '—' : `${formatNumber(summary.winRate, 1)}%`, hint: `${formatNumber(summary?.wonThisMonth ?? 0)} won, ${formatNumber(summary?.lostThisMonth ?? 0)} lost this month` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Enquiries</h1>
          <p className="mt-1 text-sm text-gray-500">Who has asked for a price, what was discussed, when to follow up, and whether it was won.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Log Enquiry
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by number, customer or prospect, contact or subject" aria-label="Search enquiries" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | 'open' | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
            <option value="open">Open</option>
            <option value="">All</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            <input type="checkbox" checked={followUpDue} onChange={(event) => { setFollowUpDue(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Follow-up due
          </label>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Enquiry</th>
                <th scope="col" className={thClass}>From</th>
                <th scope="col" className={thClass}>Asked for</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Value</th>
                <th scope="col" className={thClass}>Next follow-up</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The enquiries could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No enquiry matches' : 'No open enquiries'}</p>
                    <p className="mt-1 text-sm text-gray-500">Log an enquiry when a customer or a prospect asks for a price.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.enquiryNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{formatDate(row.enquiryDate)} · {SOURCES[row.source]}</p>
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <p className="truncate text-sm text-gray-900">
                        {row.partyName}
                        {row.isProspect && <span className={cn(badgeClass, 'ml-2 bg-gray-50 text-gray-600 ring-gray-500/20')}>Prospect</span>}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{[row.contactPerson, row.city].filter(Boolean).join(' · ') || '—'}</p>
                    </td>
                    <td className="max-w-sm px-4 py-3">
                      <p className="truncate text-sm text-gray-900" title={row.subject}>{row.subject}</p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{[row.ownerName, row.quoteNumber].filter(Boolean).join(' · ') || '—'}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{row.quoteValue !== null ? formatMoney(row.quoteValue) : row.expectedValue !== null ? formatMoney(row.expectedValue) : '—'}</td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.followUpDue ? 'font-medium text-red-600' : 'text-gray-700')}>{formatDate(row.nextFollowUpOn)}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.enquiryNumber}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      {/* One window: the form while logging or editing, the enquiry otherwise */}
      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.enquiryNumber}` : 'Log Enquiry') : viewing?.enquiryNumber ?? 'Enquiry'} size="xl">
        {formOpen ? (
          <EnquiryForm key={editing?.id ?? 'new'} enquiry={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && (
            <EnquiryPanel
              key={viewingId}
              enquiryId={viewingId}
              canEdit={canEdit}
              canQuote={canCreate}
              onEdit={(enquiry) => {
                setEditing(enquiry);
                setFormOpen(true);
              }}
              onClose={() => setViewingId(null)}
              onChanged={refreshLists}
            />
          )
        )}
      </Modal>
    </div>
  );
};

export default Enquiries;
